import {
  Perils,
  PerilWithBiRiskGroups,
  PerilWithGlassRiskGroup,
  PerilWithIndexSignature,
  PerilWithMoneyRiskGroup,
  PerilWithTheftRiskGroup,
  RiskRatingResponse,
} from '~/types/address';

/**
 * Converts a DFC risk rating string (e.g. "001A", "048A", "000A") to a number.
 * Extracts characters at index 1 and 2 (the 2nd and 3rd characters),
 * removing the leading number and trailing character.
 * Mirrors backend logic: substr(str) extracts indices 1 and 2.
 * e.g. "001A" → "01" → 1, "048A" → "48" → 48, "000A" → "00" → 0
 */
const parseRating = (rating: string): number => {
  // Extract characters at index 1 and 2 (2nd and 3rd characters)
  const extracted = rating.substring(1, 3);
  return parseInt(extracted, 10);
};

/**
 * Generic risk group transformer - DRY principle applied.
 * Transforms any peril with risk group fields from { rating: "001A" } format to numeric values.
 *
 * @param peril - The peril object with sourceType and risk group fields
 * @param riskGroupFields - Array of field names to transform (e.g., ['contentsRiskGroup', 'buildingRiskGroup'])
 * @returns Transformed object with numeric risk group values
 *
 * @example
 * transformRiskGroupPeril(
 *   { sourceType: 'QAS', contentsRiskGroup: { rating: '001A' } },
 *   ['contentsRiskGroup']
 * )
 * // Returns: { sourceType: 'QAS', contentsRiskGroup: 1 }
 */
const transformRiskGroupPeril = (
  peril: PerilWithIndexSignature,
  riskGroupFields: string[]
): Record<string, string | number> => {
  const result: Record<string, string | number> = {
    sourceType: peril.sourceType,
  };

  riskGroupFields.forEach((field) => {
    const value = peril[field];
    if (value && typeof value === 'object' && 'rating' in value) {
      result[field] = parseRating(value.rating);
    }
  });

  return result;
};

// Legacy transformation functions - now use generic transformer (backward compatible)
const transformBiRiskGroupPeril = (
  peril: PerilWithBiRiskGroups
): {
  sourceType: string;
  contentsRiskGroup?: number;
  buildingRiskGroup?: number;
  biRiskGroup?: number;
} =>
  transformRiskGroupPeril(peril as PerilWithIndexSignature, [
    'contentsRiskGroup',
    'buildingRiskGroup',
    'biRiskGroup',
  ]) as ReturnType<typeof transformBiRiskGroupPeril>;

const transformTheftRiskGroupPeril = (
  peril: PerilWithTheftRiskGroup
): { sourceType: string; theftRiskGroup?: number } =>
  transformRiskGroupPeril(peril as PerilWithIndexSignature, ['theftRiskGroup']) as ReturnType<
    typeof transformTheftRiskGroupPeril
  >;

const transformMoneyRiskGroupPeril = (
  peril: PerilWithMoneyRiskGroup
): { sourceType: string; moneyRiskGroup?: number } =>
  transformRiskGroupPeril(peril as PerilWithIndexSignature, ['moneyRiskGroup']) as ReturnType<
    typeof transformMoneyRiskGroupPeril
  >;

const transformGlassRiskGroupPeril = (
  peril: PerilWithGlassRiskGroup
): { sourceType: string; glassRiskGroup?: number } =>
  transformRiskGroupPeril(peril as PerilWithIndexSignature, ['glassRiskGroup']) as ReturnType<
    typeof transformGlassRiskGroupPeril
  >;

/**
 * Transforms a DFC Risk Rating API response into the DigiSME Perils shape.
 *
 * - Flattens each risk group from `{ rating: "001A" }` → `1` (numeric).
 */
export const transformRiskRatingResponse = (response: RiskRatingResponse): Partial<Perils> => {
  const { riskDetails } = response;

  const result = {
    ...(riskDetails.flood && {
      flood: transformBiRiskGroupPeril(riskDetails.flood) as Perils['flood'],
    }),
    ...(riskDetails.cyclone && {
      cyclone: transformBiRiskGroupPeril(riskDetails.cyclone) as Perils['cyclone'],
    }),
    ...(riskDetails.bushfire && {
      bushfire: transformBiRiskGroupPeril(riskDetails.bushfire) as Perils['bushfire'],
    }),
    ...(riskDetails.storm && {
      storm: transformBiRiskGroupPeril(riskDetails.storm) as Perils['storm'],
    }),
    ...(riskDetails.overlandFlow && {
      overlandFlow: transformBiRiskGroupPeril(riskDetails.overlandFlow) as Perils['overlandFlow'],
    }),
    ...(riskDetails.earthquake && {
      earthquake: transformBiRiskGroupPeril(riskDetails.earthquake) as Perils['earthquake'],
    }),
    ...(riskDetails.fire && {
      fire: transformBiRiskGroupPeril(riskDetails.fire) as Perils['fire'],
    }),
    ...(riskDetails.waterDamage && {
      waterDamage: transformBiRiskGroupPeril(riskDetails.waterDamage) as Perils['waterDamage'],
    }),
    ...(riskDetails.maliciousDamage && {
      maliciousDamage: transformBiRiskGroupPeril(
        riskDetails.maliciousDamage
      ) as Perils['maliciousDamage'],
    }),
    ...(riskDetails.impact && {
      impact: transformBiRiskGroupPeril(riskDetails.impact) as Perils['impact'],
    }),
    ...(riskDetails.other && {
      other: transformBiRiskGroupPeril(riskDetails.other) as Perils['other'],
    }),
    ...(riskDetails.accidentalDamage && {
      accidentalDamage: transformBiRiskGroupPeril(
        riskDetails.accidentalDamage
      ) as Perils['accidentalDamage'],
    }),
    ...(riskDetails.model && {
      model: transformBiRiskGroupPeril(
        riskDetails.model as PerilWithBiRiskGroups
      ) as Perils['model'],
    }),
    ...(riskDetails.stock && {
      stock: transformTheftRiskGroupPeril(riskDetails.stock) as Perils['stock'],
    }),
    ...(riskDetails.contents && {
      contents: transformTheftRiskGroupPeril(riskDetails.contents) as Perils['contents'],
    }),
    ...(riskDetails.tobacco && {
      tobacco: transformTheftRiskGroupPeril(riskDetails.tobacco) as Perils['tobacco'],
    }),
    ...(riskDetails.liquor && {
      liquor: transformTheftRiskGroupPeril(riskDetails.liquor) as Perils['liquor'],
    }),
    ...(riskDetails.withoutForceableEntry && {
      withoutForceableEntry: transformTheftRiskGroupPeril(
        riskDetails.withoutForceableEntry
      ) as Perils['withoutForceableEntry'],
    }),
    ...(riskDetails.theftModel && {
      theftModel: transformTheftRiskGroupPeril(riskDetails.theftModel) as Perils['theftModel'],
    }),
    ...(riskDetails.inTransit && {
      inTransit: transformMoneyRiskGroupPeril(riskDetails.inTransit) as Perils['inTransit'],
    }),
    ...(riskDetails.inResidence && {
      inResidence: transformMoneyRiskGroupPeril(riskDetails.inResidence) as Perils['inResidence'],
    }),
    ...(riskDetails.businessHours && {
      businessHours: transformMoneyRiskGroupPeril(
        riskDetails.businessHours
      ) as Perils['businessHours'],
    }),
    ...(riskDetails.afterHours && {
      afterHours: transformMoneyRiskGroupPeril(riskDetails.afterHours) as Perils['afterHours'],
    }),
    ...(riskDetails.inSafe && {
      inSafe: transformMoneyRiskGroupPeril(riskDetails.inSafe) as Perils['inSafe'],
    }),
    ...(riskDetails.moneyModel && {
      moneyModel: transformMoneyRiskGroupPeril(riskDetails.moneyModel) as Perils['moneyModel'],
    }),
    ...(riskDetails.glass && {
      glass: transformGlassRiskGroupPeril(riskDetails.glass) as Perils['glass'],
    }),
    ...(riskDetails.noSigns && {
      noSigns: transformGlassRiskGroupPeril(riskDetails.noSigns) as Perils['noSigns'],
    }),
    ...(riskDetails.glassAdditionalSI && {
      glassAdditionalSI: transformGlassRiskGroupPeril(
        riskDetails.glassAdditionalSI
      ) as Perils['glassAdditionalSI'],
    }),
    ...(riskDetails.glassModel && {
      glassModel: transformGlassRiskGroupPeril(riskDetails.glassModel) as Perils['glassModel'],
    }),
  };

  return result;
};
